'use client'
import React, { useEffect, useRef } from 'react'
import Image from 'next/image'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

type Variant = 'light' | 'dark'
type PsychCardData = {
  id: string
  title: string
  imageSrc: string
  imageAlt: string
  items: readonly string[]
  variant?: Variant
}

const conditions = [
  'Anxiety Disorders',
  'Depressive Disorders',
  'Neurodevelopmental Disorders',
  'Bipolar Disorders',
  'Obsessive-Compulsive Disorders',
  'Post-Traumatic Stress Disorders',
] as const

const therapies = [
  'Cognitive Behavioral Therapy (CBT)',
  'Dialectical Behavior Therapy (DBT)',
  'Coaching for Attention Deficit Hyperactivity Disorder (ADHD)',
  'Mindfulness-Based Therapeutic Approaches',
  'Interpersonal Psychotherapy',
] as const

const assessments = [
  'Psychoeducational Assessments',
  'Surrogacy Evaluations',
  'Psychometric Assessments',
  'Neurocognitive Assessments',
  'Diagnostic Clarification Assessments',
  'Autism / ADHD Diagnostic Batteries',
] as const

const cards: PsychCardData[] = [
  {
    id: 'conditions',
    title: 'Conditions We Treat',
    imageSrc: '/images/psychology/conditions-we-treat.webp',
    imageAlt: 'Conditions illustration',
    items: conditions,
    variant: 'light',
  },
  {
    id: 'therapies',
    title: 'Therapies We Use',
    imageSrc: '/images/psychology/therapies-we-use.webp',
    imageAlt: 'Therapies illustration',
    items: therapies,
    variant: 'dark',
  },
  {
    id: 'assessments',
    title: 'Assessments We Conduct',
    imageSrc: '/images/psychology/assessments-we-conduct.webp',
    imageAlt: 'Assessments illustration',
    items: assessments,
    variant: 'light',
  },
]

const PillHeading: React.FC<{ label: string }> = ({ label }) => (
  <div className="inline-flex rounded-3xl bg-cyan-300 px-4 pt-5 pb-3 shadow-[0px_4px_4px_#00000040]">
    <h2 className="text-2xl sm:text-3xl font-bold text-black">{label}</h2>
  </div>
)

/** Reusable card component */
const PsychCard: React.FC<{ data: PsychCardData }> = ({ data }) => {
  const isDark = data.variant === 'dark'
  return (
    <Card
      data-psych-card
      className={`h-full
        rounded-3xl border border-light-dark-mode-border-default shadow-[0px_4px_4px_#00000040]
        sm:min-h-[520px] lg:min-h-[560px]
        ${isDark ? 'bg-black text-white' : 'bg-white text-black'}
      `}
    >
      <CardContent className="flex h-full flex-col items-center gap-4 sm:gap-6 p-5 sm:p-6">
        <div className="flex flex-1 flex-col items-center gap-4 sm:gap-5">
          <Image
            src={data.imageSrc}
            alt={data.imageAlt}
            width={196}
            height={214}
            className="h-[160px] w-[148px] sm:h-[214px] sm:w-[196px] object-contain"
          />
          <h3
            className={`w-full max-w-[317px] text-center text-xl sm:text-2xl font-extrabold ${
              isDark ? 'text-white' : 'text-black'
            }`}
          >
            {data.title}
          </h3>
          <Separator
            className={`h-px w-full max-w-[317px] ${isDark ? 'bg-white' : 'bg-gray-300'}`}
          />
          <ul
            className={`w-full max-w-[317px] list-disc space-y-1 pl-5 text-sm sm:text-base ${
              isDark ? 'text-white/90' : 'text-black/90'
            }`}
          >
            {data.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}

const PsychologyServicesSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const reduce = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches
    if (reduce || !sectionRef.current) return

    const els = Array.from(
      sectionRef.current.querySelectorAll<HTMLElement>('[data-psych-card]'),
    )
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            io.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <section
      id="psychology"
      ref={sectionRef}
      className="w-full bg-white py-10 scroll-mt-24 md:scroll-mt-32"
    >
      <div className="mx-auto max-w-[1275px] px-4">
        <PillHeading label="Psychology Services" />

        {/* Flex everywhere: wraps to 1 → 2 → 3 cards across */}
        <div className="mt-6 flex flex-wrap items-stretch gap-4 sm:gap-6">
          {cards.map((card) => (
            <div
              key={card.id}
              className="
                min-w-0
                flex-[1_0_100%]
                sm:flex-[1_0_calc(50%-12px)]
                lg:flex-[1_0_calc(33.333%-16px)]
              "
            >
              <PsychCard data={card} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default PsychologyServicesSection
